import React, { useState, useRef, useEffect } from 'react';
import { X, Calendar, Clock, Users, FileText, Repeat, Video, List, Plus, ChevronDown, MapPin, Monitor, BookOpen, Save, Trash2 } from 'lucide-react';
import { meetingService } from '../../../services/meeting.service.ts';
import { topicTemplateService, TopicTemplate } from '../../../services/topicTemplate.service.ts';
import { authService } from '../../../services/auth.service.ts';
import {
  getMaxScheduledMeetingMinutes,
  getUserPlan,
  FREE_MEETING_MAX_DURATION_MINUTES,
  FREE_SCHEDULED_MEETING_MIN_MINUTES,
} from '../../../utils/plan.ts';
import { getDefaultMeetingTime } from '../../calendar/utils/calendarUtils.ts';
import {
  fetchGoogleCalendarStatus,
  pushMeetingToGoogleCalendar,
} from '../../calendar/services/googleCalendar.service.ts';

interface CreateMeetingFormProps {
  onClose: () => void;
  onCreated?: (scheduledAt?: string) => void;
  initialDate?: string;
  initialTime?: string;
  lockDate?: boolean;
}

interface Topic {
  id: string;
  title: string;
  duration: number;
  type: 'bilgilendirme' | 'karar';
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'Seçiniz',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Seçim yap: ${selectedOption?.label || placeholder}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 flex items-center justify-between"
      >
        <span className={selectedOption ? 'text-white' : 'text-gray-400'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20 overflow-hidden"
          role="listbox"
          aria-label="Seçenekler"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={value === option.value}
              className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                value === option.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-300 hover:bg-white/10'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PRO_DURATION_OPTIONS = [
  { value: '15', label: '15 dakika' },
  { value: '30', label: '30 dakika' },
  { value: '45', label: '45 dakika' },
  { value: '60', label: '1 saat' },
  { value: '90', label: '1.5 saat' },
  { value: '120', label: '2 saat' },
  { value: '180', label: '3 saat' },
  { value: '240', label: '4 saat' },
  { value: '480', label: '8 saat' },
];

function buildDurationOptions(maxMeetingMinutes: number): { value: string; label: string }[] {
  // Ücretsiz plan: 10–50 dk arasında her dakika seçilebilir
  if (maxMeetingMinutes <= FREE_MEETING_MAX_DURATION_MINUTES) {
    const min = FREE_SCHEDULED_MEETING_MIN_MINUTES;
    const max = maxMeetingMinutes;
    return Array.from({ length: max - min + 1 }, (_, i) => {
      const minutes = min + i;
      return { value: String(minutes), label: `${minutes} dakika` };
    });
  }
  return PRO_DURATION_OPTIONS.filter(
    (opt) => parseInt(opt.value, 10) <= maxMeetingMinutes
  );
}

const CreateMeetingForm: React.FC<CreateMeetingFormProps> = ({
  onClose,
  onCreated,
  initialDate,
  initialTime,
  lockDate = false,
}) => {
  const maxMeetingMinutes = getMaxScheduledMeetingMinutes(getUserPlan(authService.getUser()));
  const durationOptions = buildDurationOptions(maxMeetingMinutes);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleSyncNote, setGoogleSyncNote] = useState<string | null>(null);
  
  // Gündem Havuzu State
  const [showTopicPool, setShowTopicPool] = useState(false);
  const [topicTemplates, setTopicTemplates] = useState<TopicTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [savingToPool, setSavingToPool] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);

  const defaultDate = initialDate ?? '';
  const defaultTime =
    initialTime ??
    (initialDate ? getDefaultMeetingTime(new Date(`${initialDate}T12:00:00`)) : '');

  const [formData, setFormData] = useState({
    meetingFormat: 'online' as 'online' | 'in-person',
    title: '',
    description: '',
    topics: [] as Topic[],
    topicInput: '',
    topicDuration: '15',
    topicType: 'bilgilendirme' as 'bilgilendirme' | 'karar',
    date: defaultDate,
    time: defaultTime,
    duration: '',
    participants: [] as string[],
    participantEmail: '',
    isRecurring: false,
    recurrenceType: 'weekly',
    meetingType: 'video',
    location: '',
    address: '',
    room: '',
    building: '',
    notes: ''
  });

  // Gündem havuzunu yükle
  const loadTopicTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const [templates, cats] = await Promise.all([
        topicTemplateService.getTemplates(selectedCategory || undefined),
        topicTemplateService.getCategories()
      ]);
      setTopicTemplates(templates);
      setCategories(cats);
    } catch (err) {
      console.error('Gündem havuzu yüklenemedi:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (showTopicPool) {
      loadTopicTemplates();
    }
    // loadTopicTemplates kasıtlı olarak deps dışında: her render'da yeniden
    // tanımlanır, deps'e eklenirse sonsuz fetch loop'u oluşur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTopicPool, selectedCategory]);

  // Havuzdan konu ekle
  const addFromPool = (template: TopicTemplate) => {
    const newTopic: Topic = {
      id: Math.random().toString(36).substring(7),
      title: template.title,
      duration: template.duration,
      type: template.type as 'bilgilendirme' | 'karar'
    };
    setFormData({
      ...formData,
      topics: [...formData.topics, newTopic]
    });
  };

  // Konuları havuza kaydet
  const saveTopicsToPool = async () => {
    if (formData.topics.length === 0) return;
    
    setSavingToPool(true);
    try {
      const templates = formData.topics.map(topic => ({
        title: topic.title,
        duration: topic.duration,
        type: topic.type,
        category: newCategory || selectedCategory || undefined
      }));
      
      await topicTemplateService.createTemplates(templates);
      await loadTopicTemplates();
      setShowCategoryInput(false);
      setNewCategory('');
      alert('Konular gündem havuzuna kaydedildi!');
    } catch (err) {
      console.error('Konular kaydedilemedi:', err);
      alert('Konular kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSavingToPool(false);
    }
  };

  // Havuzdan şablon sil
  const deleteFromPool = async (templateId: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    
    try {
      await topicTemplateService.deleteTemplate(templateId);
      setTopicTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (err) {
      console.error('Şablon silinemedi:', err);
    }
  };

  const handleAddTopic = () => {
    if (!formData.topicInput.trim()) return;

    const parsed = parseInt(formData.topicDuration, 10);
    // Geçersiz / boş süre durumunda 15 dk varsayılana, sınırlar dışındaysa kırp.
    const safeDuration = Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, 1), maxMeetingMinutes)
      : 15;

    const newTopic: Topic = {
      id: Math.random().toString(36).substring(7),
      title: formData.topicInput.trim(),
      duration: safeDuration,
      type: formData.topicType
    };
    setFormData({
      ...formData,
      topics: [...formData.topics, newTopic],
      topicInput: '',
      topicDuration: '15',
      topicType: 'bilgilendirme'
    });
  };

  const handleRemoveTopic = (topicId: string) => {
    setFormData({
      ...formData,
      topics: formData.topics.filter(t => t.id !== topicId)
    });
  };

  const handleAddParticipant = () => {
    if (formData.participantEmail && !formData.participants.includes(formData.participantEmail)) {
      setFormData({
        ...formData,
        participants: [...formData.participants, formData.participantEmail],
        participantEmail: ''
      });
    }
  };

  const handleRemoveParticipant = (email: string) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter(p => p !== email)
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const durationMinutes = parseInt(formData.duration, 10);
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      setError('Lütfen toplantı süresini seçin.');
      setIsSubmitting(false);
      return;
    }

    try {
      const scheduledAt = new Date(`${formData.date}T${formData.time}`).toISOString();

      const meetingData: any = {
        title: formData.title,
        description: formData.description || undefined,
        scheduledAt,
        duration: durationMinutes,
        meetingFormat: formData.meetingFormat === 'in-person' ? 'IN_PERSON' : 'ONLINE',
        isInstant: false,
        isRecurring: formData.isRecurring,
        recurrenceType: formData.isRecurring ? formData.recurrenceType.toUpperCase() : undefined,
        participants: formData.participants.map(email => ({ email })),
        topics: formData.topics.map((topic, index) => ({
          title: topic.title,
          duration: topic.duration,
          type: topic.type,
          order: index
        }))
      };

      if (formData.meetingFormat === 'online') {
        meetingData.meetingType = formData.meetingType.toUpperCase();
      }

      if (formData.meetingFormat === 'in-person') {
        meetingData.location = formData.location;
        meetingData.address = formData.address;
        meetingData.room = formData.room;
        meetingData.building = formData.building;
        meetingData.meetingType = 'IN_PERSON';
      }

      const meeting = await meetingService.createMeeting(meetingData);
      console.log('Toplantı oluşturuldu:', meeting);

      const gcalStatus = await fetchGoogleCalendarStatus();
      if (gcalStatus.connected && meeting.scheduledAt) {
        const pushResult = await pushMeetingToGoogleCalendar({
          meetingId: String(meeting.id),
          title: meeting.title || formData.title,
          description: meeting.description ?? undefined,
          scheduledAt:
            typeof meeting.scheduledAt === 'string'
              ? meeting.scheduledAt
              : scheduledAt,
          durationMinutes: Number(meeting.duration ?? formData.duration) || 60,
          meetingFormat:
            meeting.meetingFormat === 'IN_PERSON' ? 'IN_PERSON' : 'ONLINE',
          location: meeting.location ?? undefined,
          address: meeting.address ?? undefined,
          meetingCode: meeting.meetingCode ?? undefined,
          timezone: 'Europe/Istanbul',
        });
        if (pushResult.pushed) {
          setGoogleSyncNote('Toplantı Google Takviminize de eklendi.');
        } else {
          setGoogleSyncNote('Toplantı oluşturuldu; Google Takvime eklenemedi.');
        }
      }

      if (onCreated) {
        onCreated(meeting.scheduledAt ?? scheduledAt);
      } else {
        onClose();
      }
    } catch (err: any) {
      console.error('Toplantı oluşturma hatası:', err);
      setError(err.response?.data?.message || 'Toplantı oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative pr-8 sm:pr-0">
      <button
        onClick={onClose}
        className="absolute top-0 right-0 p-2 sm:p-2 text-gray-400 hover:text-white transition rounded-lg hover:bg-white/5 touch-manipulation"
        aria-label="Formu kapat"
      >
        <X className="w-5 h-5" />
      </button>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <h3 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Yeni Toplantı Oluştur</h3>

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        {googleSyncNote && (
          <div className="p-4 bg-blue-500/15 border border-blue-500/40 rounded-lg text-blue-200 text-sm">
            {googleSyncNote}
          </div>
        )}

        {/* Toplantı Formatı Seçimi */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Toplantı Formatı *
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, meetingFormat: 'online' })}
              className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
                formData.meetingFormat === 'online'
                  ? 'border-purple-500 bg-purple-600/20 text-white'
                  : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/30 hover:text-white'
              }`}
            >
              <Monitor className="w-8 h-8 mb-2" />
              <span className="font-medium">Online</span>
              <span className="text-xs mt-1 opacity-70">Video konferans</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, meetingFormat: 'in-person' })}
              className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
                formData.meetingFormat === 'in-person'
                  ? 'border-purple-500 bg-purple-600/20 text-white'
                  : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/30 hover:text-white'
              }`}
            >
              <MapPin className="w-8 h-8 mb-2" />
              <span className="font-medium">Yüz Yüze</span>
              <span className="text-xs mt-1 opacity-70">Fiziksel mekan</span>
            </button>
          </div>
        </div>

        {/* Toplantı Başlığı */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Toplantı Başlığı *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            placeholder="Toplantı başlığını girin"
          />
        </div>

        {/* Yüz Yüze Toplantı - Mekan Bilgileri */}
        {formData.meetingFormat === 'in-person' && (
          <div className="space-y-4 p-4 bg-white/5 rounded-xl border border-white/10">
            <h4 className="text-sm font-semibold text-purple-400 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Mekan Bilgileri
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mekan Adı *
              </label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                placeholder="Örn: Ana Ofis, Toplantı Salonu A"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bina
                </label>
                <input
                  type="text"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  placeholder="Örn: A Blok"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Oda / Kat
                </label>
                <input
                  type="text"
                  value={formData.room}
                  onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  placeholder="Örn: 3. Kat, Oda 305"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Adres
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                rows={2}
                placeholder="Tam adres bilgisi"
              />
            </div>
          </div>
        )}

        {/* Tarih ve Saat */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="meeting-date" className="block text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Tarih *</span>
            </label>
            {lockDate && formData.date ? (
              <div className="w-full px-4 py-3 bg-purple-600/10 border border-purple-500/30 rounded-lg text-white">
                {new Date(`${formData.date}T12:00:00`).toLocaleDateString('tr-TR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </div>
            ) : (
              <input
                id="meeting-date"
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                min={new Date().toISOString().split('T')[0]}
              />
            )}
            {lockDate && (
              <p className="text-xs text-purple-300/80 mt-1">Takvimden seçilen tarih otomatik atandı.</p>
            )}
          </div>

          <div>
            <label htmlFor="meeting-time" className="block text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Saat *</span>
            </label>
            <input
              id="meeting-time"
              type="time"
              required
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Süre ve Toplantı Tipi */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Toplantı Süresi</label>
            <CustomSelect
              value={formData.duration}
              onChange={(value) => setFormData({ ...formData, duration: value })}
              placeholder="Toplantı Süresi"
              options={
                durationOptions.length > 0
                  ? durationOptions
                  : [{ value: String(maxMeetingMinutes), label: `${maxMeetingMinutes} dakika` }]
              }
            />
            {maxMeetingMinutes <= FREE_MEETING_MAX_DURATION_MINUTES && (
              <p className="text-xs text-gray-500 mt-1">
                Ücretsiz planda süre {FREE_SCHEDULED_MEETING_MIN_MINUTES}–{maxMeetingMinutes} dakika arasında ayarlanabilir.
              </p>
            )}
          </div>

          {formData.meetingFormat === 'online' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                <Video className="w-4 h-4" />
                <span>Toplantı Tipi</span>
              </label>
              <CustomSelect
                value={formData.meetingType}
                onChange={(value) => setFormData({ ...formData, meetingType: value })}
                options={[
                  { value: 'video', label: 'Görüntülü Toplantı' },
                  { value: 'audio', label: 'Sesli Toplantı' },
                  { value: 'screen', label: 'Ekran Paylaşımı' }
                ]}
              />
            </div>
          )}
        </div>

        {/* Açıklama */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Açıklama</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
            rows={3}
            placeholder="Toplantı hakkında notlar..."
          />
        </div>

        {/* Toplantıda Ele Alınacak Konular */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300 flex items-center space-x-2">
              <List className="w-4 h-4" />
              <span>Toplantıda Ele Alınacak Konular</span>
            </label>
            <button
              type="button"
              onClick={() => setShowTopicPool(!showTopicPool)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                showTopicPool 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Gündem Havuzu</span>
            </button>
          </div>

          {/* Gündem Havuzu Panel */}
          {showTopicPool && (
            <div className="mb-4 p-4 bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-500/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-purple-300 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Gündem Havuzu
                </h4>
                {formData.topics.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowCategoryInput(true)}
                    className="flex items-center gap-1.5 px-2 py-1 bg-green-600/20 text-green-300 rounded text-xs hover:bg-green-600/30 transition"
                  >
                    <Save className="w-3 h-3" />
                    <span>Konuları Havuza Kaydet</span>
                  </button>
                )}
              </div>

              {/* Kategori ile kaydet */}
              {showCategoryInput && (
                <div className="mb-3 p-3 bg-white/5 rounded-lg">
                  <label className="block text-xs text-gray-400 mb-2">Kategori (opsiyonel)</label>
                  <div className="flex gap-2">
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      aria-label="Kategori seç"
                      title="Kategori seç"
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Kategorisiz</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Yeni kategori"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setShowCategoryInput(false)}
                      className="flex-1 px-3 py-1.5 bg-white/10 text-gray-300 rounded text-xs hover:bg-white/20 transition"
                    >
                      İptal
                    </button>
                    <button
                      type="button"
                      onClick={saveTopicsToPool}
                      disabled={savingToPool}
                      className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition disabled:opacity-50"
                    >
                      {savingToPool ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              )}

              {/* Kategori Filtresi */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory('')}
                    className={`px-2 py-1 rounded text-xs transition ${
                      selectedCategory === '' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    Tümü
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-2 py-1 rounded text-xs transition ${
                        selectedCategory === cat 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {/* Şablonlar */}
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
                </div>
              ) : topicTemplates.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">
                  Henüz kayıtlı gündem konusu yok. Toplantıya eklediğiniz konuları havuza kaydedebilirsiniz.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {topicTemplates.map(template => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-2 bg-white/5 rounded-lg hover:bg-white/10 transition group"
                    >
                      <button
                        type="button"
                        onClick={() => addFromPool(template)}
                        className="flex-1 text-left"
                      >
                        <div className="text-white text-sm font-medium truncate">{template.title}</div>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>{template.duration} dk</span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            template.type === 'karar' 
                              ? 'bg-orange-500/20 text-orange-300' 
                              : 'bg-blue-500/20 text-blue-300'
                          }`}>
                            {template.type === 'karar' ? 'Karar' : 'Bilgi'}
                          </span>
                          {template.category && (
                            <span className="text-purple-400">{template.category}</span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteFromPool(template.id)}
                        className="p-2 min-h-[36px] min-w-[36px] flex items-center justify-center text-gray-500 hover:text-red-400 active:scale-95 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 transition flex-shrink-0"
                        aria-label="Şablonu sil"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Konu Ekleme Formu */}
          <div className="space-y-3 mb-3">
            <input
              type="text"
              value={formData.topicInput}
              onChange={(e) => setFormData({ ...formData, topicInput: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTopic();
                }
              }}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="Konu başlığı ekle"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="topic-duration" className="block text-xs font-medium text-gray-400 mb-1">
                  Süre (dakika)
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    id="topic-duration"
                    type="number"
                    min={1}
                    max={maxMeetingMinutes}
                    step={1}
                    inputMode="numeric"
                    value={formData.topicDuration}
                    onChange={(e) => setFormData({ ...formData, topicDuration: e.target.value })}
                    onBlur={(e) => {
                      // Boş bırakılırsa varsayılana dön, sınırların dışındaysa kırp.
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n)) {
                        setFormData({ ...formData, topicDuration: '15' });
                      } else if (n < 1) {
                        setFormData({ ...formData, topicDuration: '1' });
                      } else if (n > maxMeetingMinutes) {
                        setFormData({ ...formData, topicDuration: String(maxMeetingMinutes) });
                      }
                    }}
                    className="w-full pl-9 pr-14 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    placeholder="15"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                    dk
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['5', '10', '15', '30', '45', '60']
                    .filter((preset) => parseInt(preset, 10) <= maxMeetingMinutes)
                    .map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setFormData({ ...formData, topicDuration: preset })}
                      className={`px-2 py-0.5 rounded text-xs transition ${
                        formData.topicDuration === preset
                          ? 'bg-purple-600 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {preset} dk
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Konu Tipi
                </label>
                <CustomSelect
                  value={formData.topicType}
                  onChange={(value) => setFormData({ ...formData, topicType: value as 'bilgilendirme' | 'karar' })}
                  options={[
                    { value: 'bilgilendirme', label: 'Bilgilendirme' },
                    { value: 'karar', label: 'Karar Alınacak' }
                  ]}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleAddTopic}
              className="w-full px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition flex items-center justify-center space-x-2 text-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Konu Ekle</span>
            </button>
          </div>

          {/* Eklenen Konular */}
          {formData.topics.length > 0 && (
            <div className="space-y-2 mt-2">
              {formData.topics.map((topic, index) => (
                <div
                  key={topic.id}
                  className="flex items-start sm:items-center justify-between gap-3 px-3 sm:px-4 py-3 bg-white/5 border border-white/10 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-purple-600/30 text-purple-300 rounded text-xs font-medium">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium mb-1 break-words">{topic.title}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 shrink-0" />
                          <span>{topic.duration} dk</span>
                        </span>
                        <span className={`px-2 py-0.5 rounded shrink-0 ${
                          topic.type === 'karar' 
                            ? 'bg-orange-500/20 text-orange-300' 
                            : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {topic.type === 'karar' ? 'Karar Alınacak' : 'Bilgilendirme'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveTopic(topic.id)}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition touch-manipulation"
                    aria-label={`${topic.title} konusunu kaldır`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {/* Toplam Süre */}
              {(() => {
                const topicsTotal = formData.topics.reduce((sum, t) => sum + t.duration, 0);
                const meetingDuration = parseInt(formData.duration, 10) || 0;
                const exceeds = topicsTotal > meetingDuration;
                return (
                  <div
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm pt-2 ${
                      exceeds ? 'text-amber-300' : 'text-gray-400'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      Gündem toplamı: {topicsTotal} dk
                      {meetingDuration > 0 && (
                        <span className="text-gray-500">/ toplantı {meetingDuration} dk</span>
                      )}
                    </span>
                    {exceeds && (
                      <span className="text-xs text-amber-400/90">
                        Gündem maddeleri toplantı süresinden uzun; süre otomatik aşılsa da devam eder.
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Katılımcılar */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 shrink-0" />
            <span>Katılımcılar</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="email"
              value={formData.participantEmail}
              onChange={(e) => setFormData({ ...formData, participantEmail: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddParticipant();
                }
              }}
              className="flex-1 min-w-0 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="E-posta adresi ekle"
            />
            <button
              type="button"
              onClick={handleAddParticipant}
              className="w-full sm:w-auto shrink-0 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
            >
              Ekle
            </button>
          </div>
          {formData.participants.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.participants.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-2 px-3 py-1 bg-purple-600/20 text-purple-300 rounded-lg text-sm"
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveParticipant(email)}
                    className="hover:text-white transition"
                    aria-label={`${email} katılımcısını kaldır`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Tekrarlayan Toplantı */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="recurring"
            checked={formData.isRecurring}
            onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
            className="w-4 h-4 text-purple-600 bg-white/5 border-white/10 rounded focus:ring-purple-500"
          />
          <label htmlFor="recurring" className="flex items-center space-x-2 text-gray-300">
            <Repeat className="w-4 h-4" />
            <span>Tekrarlayan Toplantı</span>
          </label>
        </div>

        {formData.isRecurring && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Tekrar Sıklığı</label>
            <CustomSelect
              value={formData.recurrenceType}
              onChange={(value) => setFormData({ ...formData, recurrenceType: value })}
              options={[
                { value: 'daily', label: 'Günlük' },
                { value: 'weekly', label: 'Haftalık' },
                { value: 'monthly', label: 'Aylık' }
              ]}
            />
          </div>
        )}

        {/* Butonlar */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition text-sm disabled:opacity-50"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium transition transform hover:scale-[1.02] active:scale-[0.98] text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Oluşturuluyor...</span>
              </>
            ) : (
              <span>Toplantıyı Planla</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateMeetingForm;